import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { PerDiemRateRow, PerDiemTier } from '@/api/types'

const TIERS: PerDiemTier[] = ['Super', 'QC', 'Technician']

/** One location with its three tier rates, which is how the rate sheet reads. */
interface LocationRow {
  locationCode: string
  stateCode: string
  description: string | null
  isSiteOverride: boolean
  rates: Partial<Record<PerDiemTier, PerDiemRateRow>>
}

/**
 * Per diem rate maintenance.
 *
 * Stored as one row per location per tier, but shown as one row per location
 * with three amounts — because that is the shape of the sheet accounting
 * already keeps, and a 159-row list of location/tier pairs would be unreadable.
 *
 * Location, state and tier are not editable. They are the identity of a rate:
 * changing a location code in place would silently repoint every job that
 * resolves through it, including jobs already worked. A new location is added,
 * an obsolete one is switched off.
 */
export function PerDiemPage() {
  const [rows, setRows] = useState<PerDiemRateRow[]>([])
  const [filter, setFilter] = useState('')
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({
    location_code: '',
    state_code: '',
    description: '',
    super_amount: '',
    qc_amount: '',
    technician_amount: '',
  })

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await api.perDiemRates())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load rates')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const locations = useMemo(() => {
    const byCode = new Map<string, LocationRow>()
    for (const r of rows) {
      let loc = byCode.get(r.location_code)
      if (!loc) {
        loc = {
          locationCode: r.location_code,
          stateCode: r.state_code,
          description: r.description,
          isSiteOverride: r.is_site_override,
          rates: {},
        }
        byCode.set(r.location_code, loc)
      }
      loc.rates[r.tier] = r
    }
    const all = [...byCode.values()]
    const q = filter.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (l) =>
        l.locationCode.toLowerCase().includes(q) ||
        (l.description ?? '').toLowerCase().includes(q),
    )
  }, [rows, filter])

  async function saveRate(rate: PerDiemRateRow) {
    const raw = edits[rate.per_diem_rate_id]
    if (raw === undefined) return
    const amount = Number(raw)
    if (Number.isNaN(amount) || amount < 0) {
      return setError('Enter a rate of zero or more.')
    }
    setBusy(true)
    setError(null)
    try {
      const updated = await api.updatePerDiemRate(rate.per_diem_rate_id, {
        daily_amount: amount,
        is_active: rate.is_active,
      })
      setRows((prev) =>
        prev.map((r) =>
          r.per_diem_rate_id === updated.per_diem_rate_id ? updated : r,
        ),
      )
      setEdits((prev) => {
        const next = { ...prev }
        delete next[rate.per_diem_rate_id]
        return next
      })
      setNotice(
        `${updated.location_code} ${updated.tier} is now $${Number(
          updated.daily_amount,
        ).toFixed(2)}.`,
      )
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function addLocation() {
    setBusy(true)
    setError(null)
    try {
      setRows(
        await api.addPerDiemLocation({
          location_code: draft.location_code,
          state_code: draft.state_code,
          description: draft.description || null,
          super_amount: draft.super_amount,
          qc_amount: draft.qc_amount,
          technician_amount: draft.technician_amount,
        }),
      )
      setNotice(`Added ${draft.location_code.toUpperCase()}.`)
      setAdding(false)
      setDraft({
        location_code: '',
        state_code: '',
        description: '',
        super_amount: '',
        qc_amount: '',
        technician_amount: '',
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the location')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <div className="crew-heading">
          <h2 className="title" style={{ margin: 0 }}>
            Per diem rates
          </h2>
          <button className="btn btn-secondary" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Cancel' : '+ Add location'}
          </button>
        </div>
        <p className="muted">
          A job takes its state's rate unless it names a location in Sage
          (<span className="mono">UDF_PERDIEMLOC</span>). Rows marked{' '}
          <strong>site</strong> are only ever used by a job that names them.
        </p>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by state or location"
        />
      </div>

      {adding && (
        <div className="card">
          <h2>New location</h2>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>
            A code that differs from its state — <span className="mono">AK-DUTCH</span>{' '}
            in <span className="mono">AK</span> — is treated as a named site, so
            only jobs pointed at it will use it. All three rates are required:
            a location missing one would leave that tier silently unpaid.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div className="form-field" style={{ flex: '1 1 140px' }}>
              <label htmlFor="loc">Location code</label>
              <input
                id="loc"
                value={draft.location_code}
                onChange={(e) => setDraft({ ...draft, location_code: e.target.value })}
                placeholder="AK-DUTCH"
                maxLength={20}
              />
            </div>
            <div className="form-field" style={{ flex: '0 1 90px' }}>
              <label htmlFor="st">State</label>
              <input
                id="st"
                value={draft.state_code}
                onChange={(e) => setDraft({ ...draft, state_code: e.target.value })}
                placeholder="AK"
                maxLength={2}
              />
            </div>
            <div className="form-field" style={{ flex: '2 1 200px' }}>
              <label htmlFor="descr">Description</label>
              <input
                id="descr"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Dutch Harbor, Alaska"
                maxLength={60}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            {TIERS.map((tier) => (
              <div className="form-field" key={tier} style={{ flex: 1 }}>
                <label htmlFor={`new-${tier}`}>{tier}</label>
                <input
                  id={`new-${tier}`}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={
                    tier === 'Super'
                      ? draft.super_amount
                      : tier === 'QC'
                        ? draft.qc_amount
                        : draft.technician_amount
                  }
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [tier === 'Super'
                        ? 'super_amount'
                        : tier === 'QC'
                          ? 'qc_amount'
                          : 'technician_amount']: e.target.value,
                    })
                  }
                />
              </div>
            ))}
          </div>
          <button
            className="btn"
            style={{ marginTop: '0.75rem' }}
            onClick={addLocation}
            disabled={busy || !draft.location_code.trim() || !draft.state_code.trim()}
          >
            Add location
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Location</th>
                <th>Description</th>
                {TIERS.map((t) => (
                  <th key={t} style={{ textAlign: 'right' }}>
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {locations.map((loc) => (
                <tr key={loc.locationCode}>
                  <td className="mono">
                    {loc.locationCode}
                    {loc.isSiteOverride && (
                      <span className="muted" style={{ fontSize: '0.75rem' }}>
                        {' '}
                        site
                      </span>
                    )}
                  </td>
                  <td>{loc.description}</td>
                  {TIERS.map((tier) => {
                    const rate = loc.rates[tier]
                    if (!rate) {
                      return (
                        <td key={tier} style={{ textAlign: 'right' }} className="muted">
                          —
                        </td>
                      )
                    }
                    const pending = edits[rate.per_diem_rate_id]
                    return (
                      <td key={tier} style={{ textAlign: 'right' }}>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          style={{ width: 90, textAlign: 'right' }}
                          value={pending ?? Number(rate.daily_amount).toFixed(2)}
                          onChange={(e) =>
                            setEdits({ ...edits, [rate.per_diem_rate_id]: e.target.value })
                          }
                          // Saved on blur rather than behind a button: 159
                          // rates with a Save on each would be unusable.
                          onBlur={() => void saveRate(rate)}
                          disabled={busy}
                          aria-label={`${loc.locationCode} ${tier} rate`}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
              {locations.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Nothing matches that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
